import { useEffect, useMemo, useState } from 'react';
import { Download, Eye, Search, FileText, CheckCircle, Clock, Zap } from 'lucide-react';
import api from '../api';

// Admin view of every online enrollment with print/download links for the
// transcript and (if generated) certificate.
const StudentTranscriptsPanel = ({ apiBase }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [courses, setCourses] = useState([]);
  const [approving, setApproving] = useState({}); // { "userId-courseId": true }

  const fetchRows = async () => {
    try {
      const [tx, courseRes] = await Promise.all([
        fetch(`${apiBase}/api/admin/student-transcripts?limit=1000`),
        fetch(`${apiBase}/api/online-courses`),
      ]);
      const data = await tx.json();
      const cs = await courseRes.json();
      setRows(Array.isArray(data) ? data : []);
      setCourses(Array.isArray(cs) ? cs : []);
    } catch (_e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [tx, courseRes] = await Promise.all([
          fetch(`${apiBase}/api/admin/student-transcripts?limit=1000`),
          fetch(`${apiBase}/api/online-courses`),
        ]);
        const data = await tx.json();
        const cs = await courseRes.json();
        if (!active) return;
        setRows(Array.isArray(data) ? data : []);
        setCourses(Array.isArray(cs) ? cs : []);
      } catch (_e) {
        if (active) setRows([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [apiBase]);

  const handleAutoApprove = async (r) => {
    const key = `${r.user_id}-${r.course_id}`;
    if (approving[key]) return;
    if (!window.confirm(
      `Auto-Approve & Issue Certificate for ${r.user_name} in "${r.course_title}"?\n\n` +
      `This will mark the Mid-Term and Final Exam as PAID and PASSED at 100%, ` +
      `mark all modules complete, set the enrollment to COMPLETED, and make the ` +
      `Certificate available for download immediately.`
    )) return;
    setApproving((s) => ({ ...s, [key]: true }));
    try {
      const res = await api.post(`/admin/online-courses/${r.course_id}/auto-approve/${r.user_id}`);
      alert(res.data?.message || 'Student approved.');
      await fetchRows();
    } catch (err) {
      alert(err.response?.data?.detail || 'Auto-approve failed. Please try again.');
    } finally {
      setApproving((s) => { const c = { ...s }; delete c[key]; return c; });
    }
  };

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (courseFilter && r.course_id !== courseFilter) return false;
      if (!n) return true;
      return (
        (r.user_name || '').toLowerCase().includes(n) ||
        (r.user_email || '').toLowerCase().includes(n) ||
        (r.user_id || '').toLowerCase().includes(n) ||
        (r.course_title || '').toLowerCase().includes(n)
      );
    });
  }, [rows, q, courseFilter]);

  const stats = useMemo(() => ({
    total: rows.length,
    completed: rows.filter((r) => r.certificate_generated).length,
    inProgress: rows.filter((r) => !r.certificate_generated).length,
  }), [rows]);

  const open = (url) => window.open(`${apiBase}${url}`, '_blank', 'noopener');

  const download = async (url, filename) => {
    try {
      const r = await fetch(`${apiBase}${url}`);
      const blob = await r.blob();
      const link = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = link; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(link);
    } catch (_e) { open(url); }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch (_e) { return iso; }
  };

  return (
    <div data-testid="student-transcripts-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <h2 style={{ margin: 0, color: '#1e3a5f' }}>
            <FileText size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Student Transcripts
          </h2>
          <p style={{ color: '#64748b', marginTop: 6, marginBottom: 0, fontSize: '0.9375rem' }}>
            Every enrolled student × course pair, with printable / downloadable transcript PDFs and (where generated) Certificate of Completion PDFs.
          </p>
        </div>
        <button
          type="button"
          onClick={async () => {
            if (!window.confirm(
              'Re-issue every legacy MC-CNA verification code on cna-prep enrollments under the new MC-CMA prefix?\n\n' +
              'Old codes are preserved as aliases — anything printed in the wild still verifies. Idempotent.'
            )) return;
            try {
              const res = await api.post('/admin/online-courses/backfill-cma-codes');
              alert(res.data?.message || 'Backfill complete.');
              window.location.reload();
            } catch (err) {
              alert(err.response?.data?.detail || 'Backfill failed.');
            }
          }}
          data-testid="backfill-cma-codes-btn"
          style={{
            padding: '8px 14px',
            background: 'linear-gradient(135deg,#5c1a1b,#7c2d12)',
            color: '#fbf6e8',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: '0.82rem',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            whiteSpace: 'nowrap',
          }}
          title="Re-issue legacy MC-CNA verification codes under the new MC-CMA prefix"
        >
          Backfill CMA Codes
        </button>
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }} data-testid="transcripts-stat-strip">
        <div style={{ background: '#eff6ff', borderLeft: '3px solid #1e40af', padding: '10px 14px', borderRadius: 8, minWidth: 160 }}>
          <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 1, color: '#64748b', fontWeight: 700 }}>Total Enrollments</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1e3a5f' }} data-testid="stat-total">{stats.total}</div>
        </div>
        <div style={{ background: '#ecfdf5', borderLeft: '3px solid #15803d', padding: '10px 14px', borderRadius: 8, minWidth: 160 }}>
          <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 1, color: '#64748b', fontWeight: 700 }}>Completed · Cert Issued</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#166534' }} data-testid="stat-completed">{stats.completed}</div>
        </div>
        <div style={{ background: '#fef9c3', borderLeft: '3px solid #b45309', padding: '10px 14px', borderRadius: 8, minWidth: 160 }}>
          <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 1, color: '#64748b', fontWeight: 700 }}>In Progress</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#7c2d12' }} data-testid="stat-in-progress">{stats.inProgress}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: 380 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, course…"
            data-testid="transcript-search-input"
            style={{ width: '100%', padding: '10px 12px 10px 36px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem' }}
          />
        </div>
        <select
          value={courseFilter}
          onChange={(e) => setCourseFilter(e.target.value)}
          data-testid="transcript-course-filter"
          style={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', minWidth: 220 }}
        >
          <option value="">All courses</option>
          {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        <div style={{ alignSelf: 'center', color: '#64748b', fontSize: '0.85rem' }} data-testid="transcript-count-label">
          Showing <strong>{filtered.length}</strong> of <strong>{rows.length}</strong>
        </div>
      </div>

      {loading && <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading transcripts…</div>}

      {!loading && filtered.length === 0 && (
        <div data-testid="transcripts-empty" style={{ padding: 32, background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10, color: '#64748b', textAlign: 'center' }}>
          No matching enrollments found.
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10 }} data-testid="transcripts-table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }} data-testid="transcripts-table">
            <thead style={{ background: '#f8fafc' }}>
              <tr style={{ textAlign: 'left' }}>
                <th style={th}>Student</th>
                <th style={th}>Program</th>
                <th style={{ ...th, textAlign: 'center' }}>Modules</th>
                <th style={{ ...th, textAlign: 'center' }}>Mid · Final</th>
                <th style={{ ...th, textAlign: 'center' }}>Cert</th>
                <th style={th}>Completed</th>
                <th style={{ ...th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, idx) => (
                <tr key={`${r.user_id}-${r.course_id}-${idx}`} data-testid={`transcript-row-${r.user_id}-${r.course_id}`} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={td}>
                    <div style={{ fontWeight: 700, color: '#0b2545' }}>{r.user_name}</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{r.user_email || '—'}</div>
                    {r.apprentice_id && (
                      <div style={{ fontSize: '0.72rem', color: '#475569', fontFamily: 'ui-monospace, "SF Mono", monospace' }}>
                        Apprentice {r.apprentice_id}
                      </div>
                    )}
                  </td>
                  <td style={td}>
                    <div style={{ color: '#0b2545' }}>{r.course_title}</div>
                    <div style={{ fontSize: '0.72rem', color: '#64748b', fontFamily: 'ui-monospace, "SF Mono", monospace' }}>{r.course_id}</div>
                  </td>
                  <td style={{ ...td, textAlign: 'center', fontWeight: 700 }}>{r.modules_completed}</td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <Pill ok={r.midterm_passed} label="MID" />{' '}
                    <Pill ok={r.final_passed} label="FIN" />
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    {r.certificate_generated
                      ? <CheckCircle size={18} color="#15803d" data-testid={`cert-yes-${r.user_id}`} />
                      : <Clock size={18} color="#b45309" data-testid={`cert-no-${r.user_id}`} />}
                  </td>
                  <td style={td}>{fmtDate(r.completed_at)}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {!r.certificate_generated && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleAutoApprove(r)}
                          disabled={!!approving[`${r.user_id}-${r.course_id}`]}
                          data-testid={`auto-approve-${r.user_id}-${r.course_id}`}
                          style={btnApprove}
                          title="Auto-Approve & Issue Certificate"
                        >
                          <Zap size={14} />
                          {approving[`${r.user_id}-${r.course_id}`] ? 'Approving…' : 'Auto-Approve & Issue'}
                        </button>
                        {' '}
                      </>
                    )}
                    <button type="button" onClick={() => open(r.transcript_url)}
                      data-testid={`transcript-preview-${r.user_id}-${r.course_id}`}
                      style={btnGhost} title="Open transcript in new tab"><Eye size={14} /> Preview</button>{' '}
                    <button type="button" onClick={() => download(r.transcript_url, `transcript-${r.user_id}-${r.course_id}.pdf`)}
                      data-testid={`transcript-download-${r.user_id}-${r.course_id}`}
                      style={btnPrimary}><Download size={14} /> Transcript</button>
                    {r.certificate_url && (
                      <>
                        {' '}
                        <button type="button" onClick={() => download(r.certificate_url, `certificate-${r.user_id}-${r.course_id}.pdf`)}
                          data-testid={`cert-download-${r.user_id}-${r.course_id}`}
                          style={btnGold}><Download size={14} /> Certificate</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Pill = ({ ok, label }) => (
  <span style={{
    display: 'inline-block', padding: '2px 6px', borderRadius: 4,
    fontSize: '0.65rem', fontWeight: 800, letterSpacing: 0.5,
    background: ok ? '#dcfce7' : '#fee2e2', color: ok ? '#166534' : '#991b1b',
  }}>{label} {ok ? '✓' : '✗'}</span>
);

const th = { padding: '10px 12px', fontWeight: 700, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.5 };
const td = { padding: '12px', verticalAlign: 'top', color: '#0b2545' };
const btnPrimary = { padding: '6px 10px', background: '#0d9488', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 };
const btnGhost   = { padding: '6px 10px', background: '#f1f5f9', color: '#0b2545', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 };
const btnGold    = { padding: '6px 10px', background: 'linear-gradient(135deg,#b45309,#7c2d12)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 };
const btnApprove = { padding: '6px 10px', background: 'linear-gradient(135deg,#16a34a,#065f46)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 };

export default StudentTranscriptsPanel;
